import { useCallback, useEffect, useState } from 'react';
import { useCoreClient, useGetPaginationState } from '@hooks';
import { couponCodesState, notificationErrorState } from '@states';
import { CouponCodeStatus, CouponCodeSyncType } from '@givz/core-client';
import { useRecoilState } from 'recoil';
import * as Sentry from '@sentry/browser';

export const useFetchCouponCodes = () => {
  const [couponCodes, setCouponCodes] = useRecoilState(couponCodesState);
  const [, setNotificationError] = useRecoilState(notificationErrorState);
  const [loading, setLoading] = useState(false);
  const [fullSyncLoading, setFullSyncLoading] = useState(false);
  const [couponCodeSearch, setCouponCodeSearch] = useState('');
  const [previousCouponCodeSearch, setPreviousCouponCodeSearch] = useState('');
  const [couponCodeSyncType, setCouponCodeSyncType] = useState<
    CouponCodeSyncType | undefined
  >();
  const [couponCodeStatusFilters, setCouponCodeStatusFilters] = useState<
    CouponCodeStatus[]
  >([CouponCodeStatus.Active, CouponCodeStatus.Scheduled]);
  const {
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setTotalPages,
    totalPages,
  } = useGetPaginationState();

  const coreClient = useCoreClient();

  const fetchCouponCodes = useCallback(
    async (props: { page?: number }) => {
      const { page } = props;

      const syncType = couponCodeSyncType;
      if (syncType) {
        setCouponCodeSyncType(undefined);
      }

      if (syncType === CouponCodeSyncType.Full) {
        setFullSyncLoading(true);
      } else {
        setLoading(true);
      }

      try {
        const { data, errors } = await coreClient().CouponCodes.search({
          code: couponCodeSearch === '' ? undefined : couponCodeSearch,

          page: page ?? currentPage,
          /* Manually set a high page size limit until we decide on paging for resource modal */
          pageSize: 250,
          status: couponCodeStatusFilters,
          syncType,
        });

        if (syncType === CouponCodeSyncType.Full) {
          setFullSyncLoading(false);
        } else {
          setLoading(false);
        }

        if (errors) {
          setNotificationError(errors);
        } else {
          setPreviousCouponCodeSearch(couponCodeSearch);

          setCouponCodes(
            data.couponCodes.map((couponCode) => {
              return {
                ...couponCode,
                id: couponCode.id.toString(),
              };
            }),
          );
          setTotalPages(data.meta.totalPages);
        }
      } catch (error) {
        console.error('fetchCouponCodes ERROR:');
        console.error(error);
        Sentry.captureException(error);
        setNotificationError('Error fetching discount codes');
        if (syncType === CouponCodeSyncType.Full) {
          setFullSyncLoading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      coreClient,
      couponCodeSearch,
      couponCodeStatusFilters,
      couponCodeSyncType,
      currentPage,
      setCouponCodes,
      setNotificationError,
      setTotalPages,
    ],
  );

  useEffect(() => {
    fetchCouponCodes({});
  }, [pageSize, currentPage, couponCodeStatusFilters, fetchCouponCodes]);

  useEffect(() => {
    if (couponCodeSyncType) {
      fetchCouponCodes({});
    }
  }, [couponCodeSyncType, fetchCouponCodes]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        (couponCodeSearch === '' && previousCouponCodeSearch !== '') ||
        previousCouponCodeSearch !== couponCodeSearch
      ) {
        fetchCouponCodes({ page: 1 });
      }
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [couponCodeSearch, fetchCouponCodes, previousCouponCodeSearch]);

  return {
    couponCodeSearch,
    couponCodeStatusFilters,
    couponCodes,
    couponCodesFullSyncLoading: fullSyncLoading,
    couponCodesLoading: loading,
    currentPage,
    pageSize,
    setCouponCodeSearch,
    setCouponCodeStatusFilters,
    setCouponCodeSyncType,
    setCurrentPage,
    setPageSize,
    totalPages,
  };
};
